// -----------------------------------------------------------------------------
// src/features/reports/reportSlice.js (WITH PAGINATION AND COMMENTS)
// -----------------------------------------------------------------------------
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchReportsThunk,
  createReportThunk,
  updateReportThunk,
  fetchReportThunkById,
  fetchMonthlyStatsThunk,
  fetchIncidentTypeCountsThunk,
  fetchTopEmployeesThunk,
  fetchIncidentTypeDailyThunk,
  fetchReportsByDateRangeThunk,
  fetchDateRangeStatsThunk,
  fetchReportCommentsThunk,
  addReportCommentThunk,
  fetchReportTimelineThunk,
} from './reportThunks';

const initialState = {
  reports: [],
  loading: false,
  error: null,
  next: null,
  count: 0,
  
  // Page-based pagination
  currentPage: 1,
  totalPages: 0,
  itemsPerPage: 20,
  
  // Current filters applied
  filters: {
    storeFilter: null,
    statusFilter: null,
    employeeFilter: null,
    severityFilter: null,
    searchFilter: null,
    dateRange: {
      startDate: null,
      endDate: null,
      period: 'current_month'
    }
  },

  // Analytics with enhanced date filtering
  monthlyStats: {
    month: '',
    year: 0,
    totalReports: 0,
    statusBreakdown: {},
    dateRange: {
      startDate: null,
      endDate: null
    },
    loading: false,
    error: null,
  },
  
  incidentTypeCounts: {
    counts: {},
    severityFilter: null,
    storeFilter: null,
    dateRange: {
      startDate: null,
      endDate: null
    },
    loading: false,
    error: null,
  },
  
  topEmployees: {
    list: [],
    severityFilter: null,
    storeFilter: null,
    dateRange: {
      startDate: null,
      endDate: null
    },
    loading: false,
    error: null,
  },

  incidentTypeDaily: {
    data: {},
    startDate: null,
    endDate: null,
    employee: null,
    storeFilter: null,
    loading: false,
    error: null,
  },

  dateRangeStats: {
    data: {},
    loading: false,
    error: null,
    startDate: null,
    endDate: null
  },

  // Comments state
  comments: {
    byReportId: {}, // { reportId: { comments: [], loading: false, error: null } }
    addingComment: false,
    addCommentError: null
  },

  // Activity log / timeline state
  timeline: {
    // { [reportId]: { entries: [], loading: false, error: null, count: 0 } }
    byReportId: {},
  },
};

const reportSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    resetReports(state) {
      state.reports = [];
      state.next = null;
      state.count = 0;
      state.error = null;
      state.currentPage = 1;
      state.totalPages = 0;
      state.filters = initialState.filters;
    },
    
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },

    updateFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },

    updateDateRange(state, action) {
      state.filters.dateRange = { ...state.filters.dateRange, ...action.payload };
      state.currentPage = 1;
    },

    resetMonthlyStats(state) {
      state.monthlyStats = { ...initialState.monthlyStats };
    },
    
    resetIncidentTypeCounts(state) {
      state.incidentTypeCounts = { ...initialState.incidentTypeCounts };
    },
    
    resetTopEmployees(state) {
      state.topEmployees = { ...initialState.topEmployees };
    },
    
    resetIncidentTypeDaily(state) {
      state.incidentTypeDaily = { ...initialState.incidentTypeDaily };
    },

    resetDateRangeStats(state) {
      state.dateRangeStats = { ...initialState.dateRangeStats };
    },

    resetComments(state, action) {
      const reportId = action.payload;
      if (reportId) {
        delete state.comments.byReportId[reportId];
      } else {
        state.comments.byReportId = {};
      }
    },

    resetTimeline(state, action) {
      const reportId = action.payload;
      if (reportId) {
        delete state.timeline.byReportId[reportId];
      } else {
        state.timeline.byReportId = {};
      }
    },
    
    clearError(state) {
      state.error = null;
    },
  },
  
  extraReducers: (builder) => {
    builder
      // Reports list
      .addCase(fetchReportsThunk.pending, (state, action) => { 
        state.loading = true;
        state.error = null;
        console.log('Fetch reports pending - params:', action.meta.arg);
      })
      .addCase(fetchReportsThunk.fulfilled, (state, action) => {
        const { results, next, count } = action.payload;
        const requestParams = action.meta.arg || {};
        
        console.log('Fetch reports fulfilled - results count:', results.length, 'total count:', count);
        
        state.loading = false;
        state.error = null;
        state.reports = results;
        state.next = next;
        state.count = count;
        state.totalPages = Math.ceil(count / state.itemsPerPage);
        
        if (requestParams.store_id) state.filters.storeFilter = requestParams.store_id;
        if (requestParams.status) state.filters.statusFilter = requestParams.status;
        if (requestParams.employee_id) state.filters.employeeFilter = requestParams.employee_id;
        if (requestParams.severity) state.filters.severityFilter = requestParams.severity;
        if (requestParams.search) state.filters.searchFilter = requestParams.search;
        if (requestParams.start_date) state.filters.dateRange.startDate = requestParams.start_date;
        if (requestParams.end_date) state.filters.dateRange.endDate = requestParams.end_date;
        
        console.log('Updated pagination - page:', state.currentPage, 'totalPages:', state.totalPages, 'count:', count);
      })
      .addCase(fetchReportsThunk.rejected, (state, action) => { 
        state.loading = false;
        state.error = typeof action.payload === 'object' ? action.payload : { message: action.payload || 'Failed to fetch reports' };
        console.error('Fetch reports failed:', state.error);
      })

      // Monthly stats
      .addCase(fetchMonthlyStatsThunk.pending, (s) => { 
        s.monthlyStats.loading = true; 
        s.monthlyStats.error = null; 
      })
      .addCase(fetchMonthlyStatsThunk.fulfilled, (s, a) => {
        const { month, year, total_reports, status_breakdown } = a.payload;
        const requestParams = a.meta.arg || {};
        
        s.monthlyStats = {
          loading: false,
          error: null,
          month,
          year,
          totalReports: total_reports,
          statusBreakdown: status_breakdown,
          dateRange: {
            startDate: requestParams.start_date || null,
            endDate: requestParams.end_date || null
          }
        };
      })
      .addCase(fetchMonthlyStatsThunk.rejected, (s, a) => { 
        s.monthlyStats.loading = false; 
        s.monthlyStats.error = typeof a.payload === 'object' ? a.payload : { message: a.payload || 'Failed to fetch monthly stats' };
        console.error('Fetch monthly stats failed:', s.monthlyStats.error);
      })

      // Single report
      .addCase(fetchReportThunkById.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(fetchReportThunkById.fulfilled, (s, a) => {
        s.loading = false;
        const idx = s.reports.findIndex(r => r.id === a.payload.id);
        idx === -1 ? s.reports.push(a.payload) : (s.reports[idx] = a.payload);
        s.error = null;
      })
      .addCase(fetchReportThunkById.rejected, (s, a) => { 
        s.loading = false; 
        s.error = typeof a.payload === 'object' ? a.payload : { message: a.payload || 'Failed to fetch report' };
        console.error('Fetch report by ID failed:', s.error);
      })

      // Create report
      .addCase(createReportThunk.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(createReportThunk.fulfilled, (s, a) => { 
        s.loading = false; 
        if (s.currentPage === 1) {
          s.reports.unshift(a.payload);
          if (s.reports.length > s.itemsPerPage) {
            s.reports.pop();
          }
        }
        s.count += 1;
        s.totalPages = Math.ceil(s.count / s.itemsPerPage);
        s.error = null;
        console.log('Report created and handled for pagination:', a.payload);
      })
      .addCase(createReportThunk.rejected, (s, a) => { 
        s.loading = false; 
        s.error = typeof a.payload === 'object' ? a.payload : { message: a.payload || 'Failed to create report' };
        console.error('Create report failed:', s.error);
      })

      // Update report
      .addCase(updateReportThunk.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(updateReportThunk.fulfilled, (s, a) => {
        s.loading = false;
        const idx = s.reports.findIndex(r => r.id === a.payload.id);
        if (idx !== -1) {
          s.reports[idx] = a.payload;
          console.log('Report updated in pagination state:', a.payload);
        }
        s.error = null;
      })
      .addCase(updateReportThunk.rejected, (s, a) => { 
        s.loading = false; 
        s.error = typeof a.payload === 'object' ? a.payload : { message: a.payload || 'Failed to update report' };
        console.error('Update report failed:', s.error);
      })

      // Incident type counts
      .addCase(fetchIncidentTypeCountsThunk.pending, (s) => { 
        s.incidentTypeCounts.loading = true; 
        s.incidentTypeCounts.error = null; 
      })
      .addCase(fetchIncidentTypeCountsThunk.fulfilled, (s, a) => {
        const requestParams = a.meta.arg || {};
        
        s.incidentTypeCounts = {
          loading: false,
          error: null,
          counts: a.payload.incident_type_counts,
          severityFilter: a.payload.severity_filter,
          storeFilter: a.payload.store_filter,
          dateRange: {
            startDate: requestParams.start_date || null,
            endDate: requestParams.end_date || null
          }
        };
      })
      .addCase(fetchIncidentTypeCountsThunk.rejected, (s, a) => { 
        s.incidentTypeCounts.loading = false; 
        s.incidentTypeCounts.error = typeof a.payload === 'object' ? a.payload : { message: a.payload || 'Failed to fetch incident type counts' };
        console.error('Fetch incident type counts failed:', s.incidentTypeCounts.error);
      })

      // Top employees
      .addCase(fetchTopEmployeesThunk.pending, (s) => { 
        s.topEmployees.loading = true; 
        s.topEmployees.error = null; 
      })
      .addCase(fetchTopEmployeesThunk.fulfilled, (s, a) => {
        const requestParams = a.meta.arg || {};
        
        s.topEmployees = {
          loading: false,
          error: null,
          list: a.payload.top_5_employees,
          severityFilter: a.payload.severity_filter,
          storeFilter: a.payload.store_filter,
          dateRange: {
            startDate: requestParams.start_date || null,
            endDate: requestParams.end_date || null
          }
        };
      })
      .addCase(fetchTopEmployeesThunk.rejected, (s, a) => { 
        s.topEmployees.loading = false; 
        s.topEmployees.error = typeof a.payload === 'object' ? a.payload : { message: a.payload || 'Failed to fetch top employees' };
        console.error('Fetch top employees failed:', s.topEmployees.error);
      })

      // Daily incident-type counts
      .addCase(fetchIncidentTypeDailyThunk.pending, (s) => { 
        s.incidentTypeDaily.loading = true; 
        s.incidentTypeDaily.error = null; 
      })
      .addCase(fetchIncidentTypeDailyThunk.fulfilled, (s, a) => {
        const { start_date, end_date, data, store_filter } = a.payload;
        const requestParams = a.meta.arg || {};
        
        s.incidentTypeDaily = {
          loading: false,
          error: null,
          data,
          startDate: start_date,
          endDate: end_date,
          employee: requestParams.employee || null,
          storeFilter: store_filter || requestParams.store_id || null,
        };
      })
      .addCase(fetchIncidentTypeDailyThunk.rejected, (s, a) => { 
        s.incidentTypeDaily.loading = false; 
        s.incidentTypeDaily.error = typeof a.payload === 'object' ? a.payload : { message: a.payload || 'Failed to fetch daily incident type data' };
        console.error('Fetch incident type daily failed:', s.incidentTypeDaily.error);
      })

      // Date range reports
      .addCase(fetchReportsByDateRangeThunk.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(fetchReportsByDateRangeThunk.fulfilled, (s, a) => {
        s.loading = false;
        s.error = null;
        console.log('Date range reports fetched:', a.payload);
      })
      .addCase(fetchReportsByDateRangeThunk.rejected, (s, a) => { 
        s.loading = false; 
        s.error = typeof a.payload === 'object' ? a.payload : { message: a.payload || 'Failed to fetch date range reports' };
      })

      // Date range stats
      .addCase(fetchDateRangeStatsThunk.pending, (s) => { 
        s.dateRangeStats.loading = true; 
        s.dateRangeStats.error = null; 
      })
      .addCase(fetchDateRangeStatsThunk.fulfilled, (s, a) => {
        const requestParams = a.meta.arg || {};
        
        s.dateRangeStats = {
          loading: false,
          error: null,
          data: a.payload,
          startDate: requestParams.start_date,
          endDate: requestParams.end_date
        };
      })
      .addCase(fetchDateRangeStatsThunk.rejected, (s, a) => { 
        s.dateRangeStats.loading = false; 
        s.dateRangeStats.error = typeof a.payload === 'object' ? a.payload : { message: a.payload || 'Failed to fetch date range stats' };
      })

      // Fetch comments
      .addCase(fetchReportCommentsThunk.pending, (state, action) => {
        const reportId = action.meta.arg;
        if (!state.comments.byReportId[reportId]) {
          state.comments.byReportId[reportId] = { comments: [], loading: false, error: null };
        }
        state.comments.byReportId[reportId].loading = true;
        state.comments.byReportId[reportId].error = null;
      })
      .addCase(fetchReportCommentsThunk.fulfilled, (state, action) => {
        const { report_id, comments } = action.payload;
        state.comments.byReportId[report_id] = {
          comments,
          loading: false,
          error: null
        };
      })
      .addCase(fetchReportCommentsThunk.rejected, (state, action) => {
        const reportId = action.meta.arg;
        if (state.comments.byReportId[reportId]) {
          state.comments.byReportId[reportId].loading = false;
          state.comments.byReportId[reportId].error = action.payload;
        }
      })

      // Add comment
      .addCase(addReportCommentThunk.pending, (state) => {
        state.comments.addingComment = true;
        state.comments.addCommentError = null;
      })
      .addCase(addReportCommentThunk.fulfilled, (state, action) => {
        const { reportId, comment } = action.payload;
        state.comments.addingComment = false;
        
        if (state.comments.byReportId[reportId]) {
          state.comments.byReportId[reportId].comments.unshift(comment);
        }
      })
      .addCase(addReportCommentThunk.rejected, (state, action) => {
        state.comments.addingComment = false;
        state.comments.addCommentError = action.payload;
      })

      // Fetch timeline
      .addCase(fetchReportTimelineThunk.pending, (state, action) => {
        const reportId = action.meta.arg;
        if (!state.timeline.byReportId[reportId]) {
          state.timeline.byReportId[reportId] = { entries: [], loading: false, error: null, count: 0 };
        }
        state.timeline.byReportId[reportId].loading = true;
        state.timeline.byReportId[reportId].error = null;
      })
      .addCase(fetchReportTimelineThunk.fulfilled, (state, action) => {
        const { reportId, timeline, count } = action.payload;
        state.timeline.byReportId[reportId] = {
          entries: timeline,
          loading: false,
          error: null,
          count,
        };
      })
      .addCase(fetchReportTimelineThunk.rejected, (state, action) => {
        const reportId = action.meta.arg;
        if (state.timeline.byReportId[reportId]) {
          state.timeline.byReportId[reportId].loading = false;
          state.timeline.byReportId[reportId].error = action.payload;
        }
      });
  },
});

export const {
  resetReports,
  setCurrentPage,
  updateFilters,
  updateDateRange,
  resetMonthlyStats,
  resetIncidentTypeCounts,
  resetTopEmployees,
  resetIncidentTypeDaily,
  resetDateRangeStats,
  resetComments,
  resetTimeline,
  clearError,
} = reportSlice.actions;

export default reportSlice.reducer;